import { ElMessageBox } from "element-plus";

type DialogType = "error" | "success" | "warning" | "info";

const CONFIRM_DIALOG_CLASS = "oneteg-confirm-message-box";
const CONFIRM_DIALOG_CLOSE_TIMEOUT_MS = 400;

const activeConfirmDialogs = new Map<string, Promise<boolean>>();
let confirmDialogId = 0;

function getConfirmDialogKey(type: DialogType, title: string, message: string) {
  return JSON.stringify([type, title, message]);
}

function isDialogVisible(className: string) {
  return document.getElementsByClassName(className).length > 0;
}

function waitForConfirmDialogToVanish(className: string): Promise<void> {
  if (
    typeof document === "undefined" ||
    typeof window === "undefined" ||
    !isDialogVisible(className)
  ) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let timeoutId: number | undefined;
    let observer: MutationObserver | null = null;

    const finish = () => {
      if (observer) observer.disconnect();
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      resolve();
    };

    if (typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(() => {
        if (!isDialogVisible(className)) {
          finish();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    timeoutId = window.setTimeout(finish, CONFIRM_DIALOG_CLOSE_TIMEOUT_MS);
  });
}

async function runConfirmDialog(
  type: DialogType,
  title: string,
  message: string
) {
  const instanceClassName = `${CONFIRM_DIALOG_CLASS}-${confirmDialogId++}`;
  let isConfirmed = false;

  try {
    await ElMessageBox.confirm(
      `<div style="max-height: 200px; overflow-y: auto;">${message}</div>`,
      title,
      {
        type,
        confirmButtonText: "OK",
        cancelButtonText: "Cancel",
        showClose: true,
        customClass: `${CONFIRM_DIALOG_CLASS} ${instanceClassName}`,
        dangerouslyUseHTMLString: true,
      }
    );
    isConfirmed = true;
  } catch {
    isConfirmed = false;
  } finally {
    await waitForConfirmDialogToVanish(instanceClassName);
  }

  return isConfirmed;
}

export async function showConfirm(
  type: DialogType,
  title: string,
  message: string
): Promise<boolean> {
  const key = getConfirmDialogKey(type, title, message);
  const activeDialog = activeConfirmDialogs.get(key);

  if (activeDialog) {
    return activeDialog;
  }

  const confirmDialog = runConfirmDialog(type, title, message).finally(() => {
    activeConfirmDialogs.delete(key);
  });

  activeConfirmDialogs.set(key, confirmDialog);

  return confirmDialog;
}
